import { Spinner } from "@via/components/loading/loading.tsx";
import { resetPasswordOptions } from "@via/utils/form-validators.tsx";
import type {
  ResetPasswordPayload,
  SetNewPasswordPayload,
} from "@via/utils/interfaces.tsx";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  useSetNewPassword,
  useVerifyPasswordResetToken,
} from "@via/api/auth.api.tsx";
import Logo from "/logo.svg";
import PasswordInput from "@via/components/password-input/password-input.tsx";

export default function SetNewPassword(): JSX.Element {
  const { mutate: verifyPasswordResetToken } = useVerifyPasswordResetToken();
  const { mutate: setNewPassword, isPending: isSetNewPasswordLoading } =
    useSetNewPassword();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordPayload>({ mode: "onChange" });

  // A function to parse query parameters
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token") as string;

  useEffect(() => {
    if (!token) {
      navigate("/login");
    }
    verifyPasswordResetToken({ token });
  }, [token]);

  const handleSetNewPassword = (form: ResetPasswordPayload): void => {
    const { password } = form;
    const payload: SetNewPasswordPayload = {
      token,
      newPassword: password,
    };
    setNewPassword(payload);
  };

  return (
    <>
      <div className="dotted flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/">
            <img
              className="mx-auto h-10 w-auto duration-200 ease-in-out hover:rotate-180"
              src={Logo}
              alt="Your Company"
            />
          </Link>
          <h2 className="via-h2 mt-6 text-2xl font-bold leading-9 tracking-tight">
            Set Your New Password
          </h2>
          <p className="mt-4">Enter your new password below</p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form
              className="space-y-6"
              onSubmit={e => {
                e.preventDefault();
                void handleSubmit(handleSetNewPassword)(e);
              }}>
              <PasswordInput
                register={register}
                registerValue={"password"}
                watch={watch}
                errors={errors}
              />

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium leading-6 text-gray-900">
                  Confirm New Password
                </label>
                <div className="mt-2">
                  <input
                    {...register("confirmPassword", {
                      ...resetPasswordOptions.confirmPassword,
                      validate: value =>
                        value === watch("password") || "Passwords must match",
                    })}
                    type="password"
                    className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-200 ease-in-out placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  {errors.confirmPassword && (
                    <span className="text-xs text-rose-500">
                      {errors.confirmPassword?.message}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSetNewPasswordLoading}
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  <Spinner loading={isSetNewPasswordLoading}>
                    Set New Password
                  </Spinner>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
